// Global Instructions Rule Applied!
import React, { createContext, useContext, type ReactNode } from 'react'
import { useTabManager } from './useTabManager'

/** What TabManagerProvider publishes: the tab manager hook's value. */
export type TabManager = ReturnType<typeof useTabManager>

/**
 * Tab Manager Context
 *
 * Provides tab management functionality throughout the app
 * Wraps the useTabManager hook to make it available via context
 */
// Exported for tests that need to stand in a tab manager without the router.
export const TabManagerContext = createContext<TabManager | null>(null)

export function useTabManagerContext(): TabManager {
  const context = useContext(TabManagerContext)
  if (!context) {
    throw new Error('useTabManagerContext must be used within a TabManagerProvider')
  }
  return context
}

/**
 * Same context, but null outside a TabManagerProvider instead of throwing.
 * For components that can render without the app shell (unit tests, the
 * mobile layout) and only want tab navigation when it exists — the agent
 * signal / memory links degrade to plain text without it.
 */
export function useOptionalTabManagerContext(): TabManager | null {
  return useContext(TabManagerContext) ?? null
}

export function TabManagerProvider({ children }: { children: ReactNode }) {
  const tabManager = useTabManager()

  return <TabManagerContext.Provider value={tabManager}>{children}</TabManagerContext.Provider>
}
